/* Copyright (c) 2020-present CloudIO Inc */

import {
  emptyFunction,
  useEvent,
  useStateXAtomSetter,
} from '@cloudio-saas/statex';
import { BaseProps, Popover } from 'componentregistry';
import {
  PopoverProps,
  manualPopoverOpenAtom,
} from 'components/Popover/Popover';
import { useDeveloperMode } from 'designer/atoms';
import { IORef, useIORef } from 'designer/useIORef';
import useDebounce from 'hooks/useDebounce';
import useId from 'hooks/useId';
import { extractDevProps } from 'platform/DevProps';
import { useBooleanProperty } from 'platform/PlatformHooks';
import React, { useRef } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Props = BaseProps<any>;

let timer: NodeJS.Timeout;
export function popoverDebounce(func: (...args: any[]) => void, timeout = 350) {
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

export function usePopover(props: Props) {
  const { appUid, children, pageId, platform } = props;
  const anchorId = useId();
  const popover = Array.isArray(children)
    ? (children.find(
        (c) => c?.props?.itemType === 'Popover',
      ) as React.ReactElement<PopoverProps>)
    : null;
  const devMode = useDeveloperMode(appUid, pageId);
  const popoverProps = popover?.props;
  const popoverId = popover?.props.itemId;
  const devProps = extractDevProps(props);
  const openOnMouseEnter = useBooleanProperty({
    appUid,
    pageId,
    itemId: popoverId ?? '',
    prop: 'openOnMouseEnter',
  });
  const closeOnMouseLeave = useBooleanProperty({
    appUid,
    pageId,
    itemId: popoverId ?? '',
    prop: 'closeOnMouseLeave',
  });
  const closeOnOutSideClick = useBooleanProperty({
    appUid,
    pageId,
    itemId: popoverId ?? '',
    prop: 'closeOnOutSideClick',
  });

  const setManualPopoverOpen = useStateXAtomSetter(manualPopoverOpenAtom, {
    params: {
      appUid,
      pageId,
      popoverId: popoverId ?? '',
    },
  });

  const pref = useIORef<HTMLOrSVGElement>(null);
  const onHoverRef = useRef(false);
  const [hide, cancelHide] = useDebounce(
    popoverId
      ? (openCounter: number) => {
          const currentOpenCounter = platform.getItemProp<Popover.Popover>(
            appUid,
            pageId,
            popoverId,
            'openCounter',
          ) as number;
          if (currentOpenCounter === openCounter) {
            setManualPopoverOpen({ currentAnchorId: anchorId, open: false });
            platform.setRuntimeProp(appUid, pageId, popoverId, 'open', false);
          }
        }
      : emptyFunction,
  );
  const [show, cancelShow] = useDebounce(
    popoverId
      ? () => {
          platform.setRuntimeProp(appUid, pageId, popoverId, 'open', true);
        }
      : emptyFunction,
  );

  const onClose = useEvent(
    (e: MouseEvent | TouchEvent, reason?: 'OutSideClick') => {
      if (devMode || !popoverId) return;
      if (reason === 'OutSideClick' && !closeOnOutSideClick) {
        return;
      }
      const openCounter = platform.getItemProp<Popover.Popover>(
        appUid,
        pageId,
        popoverId,
        'openCounter',
      ) as number;
      cancelShow();
      hide(0, openCounter);
    },
  );

  const onMouseEnter = useEvent(() => {
    if (popoverId) {
      setManualPopoverOpen({ currentAnchorId: anchorId, open: true });
      platform.setRuntimeProp<Popover.Popover>(
        appUid,
        pageId,
        popoverId,
        'openCounter',
        (v) => ((v ?? 0) as number) + 1,
      );
      cancelHide();
      const delay = platform.getItemProp<Popover.Popover>(
        appUid,
        pageId,
        popoverId,
        'delay',
      ) as number;
      show(delay ?? 500);
    }
  });

  const onMouseLeave = useEvent(() => {
    if (popoverId && !onHoverRef.current && closeOnMouseLeave) {
      const openCounter = platform.getItemProp<Popover.Popover>(
        appUid,
        pageId,
        popoverId,
        'openCounter',
      ) as number;
      cancelShow();
      hide(500, openCounter);
    }
  });

  const onPopoverMouseEnter = useEvent(() => {
    if (popoverId) {
      onHoverRef.current = true;
      platform.setRuntimeProp<Popover.Popover>(
        appUid,
        pageId,
        popoverId,
        'openCounter',
        (v) => ((v ?? 0) as number) + 1,
      );
      cancelHide();
      show(0);
    }
  });

  const onPopoverMouseLeave = useEvent(() => {
    if (popoverId && closeOnMouseLeave) {
      onHoverRef.current = false;
      const openCounter = platform.getItemProp<Popover.Popover>(
        appUid,
        pageId,
        popoverId,
        'openCounter',
      ) as number;
      cancelShow();
      hide(500, openCounter);
    }
  });

  return popoverId
    ? {
        onMouseEnter:
          devMode || !openOnMouseEnter ? devProps.onMouseEnter : onMouseEnter,
        onMouseLeave:
          devMode || !closeOnMouseLeave ? devProps.onMouseLeave : onMouseLeave,
        pref,
        popoverProps,
        popover: (
          <IOPopover
            popover={popover}
            pref={pref}
            onMouseEnter={devMode ? undefined : onPopoverMouseEnter}
            onMouseLeave={devMode ? undefined : onPopoverMouseLeave}
            onClose={onClose}
            anchorId={anchorId}
          />
        ),
        otherChildren: Array.isArray(children)
          ? children.filter((c) => c.props.itemType !== 'Popover')
          : children,
      }
    : {
        onMouseEnter: devMode ? devProps.onMouseEnter : undefined,
        onMouseLeave: devMode ? devProps.onMouseLeave : undefined,
        pref,
        popover: null,
        otherChildren: children,
      };
}

function IOPopover({
  popover,
  pref,
  onMouseEnter,
  onMouseLeave,
  onClose,
  anchorId,
}: {
  popover: React.ReactElement<PopoverProps>;
  pref: IORef<HTMLOrSVGElement>;
  onMouseEnter:
    | ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void)
    | undefined;
  onMouseLeave:
    | ((e: React.MouseEvent<HTMLElement, MouseEvent>) => void)
    | undefined;
  onClose: (e: MouseEvent | TouchEvent, reason?: 'OutSideClick') => void;
  anchorId?: string;
}) {
  return React.cloneElement(popover, {
    anchorId,
    anchorRef: pref,
    onMouseEnter,
    onMouseLeave,
    onClose,
  });
}
